/**
 * Header Class
 */
export class Header {

	/**
	 * Html element.
	 * @type {HTMLElement|null}
	 */
	#root

	/**
	 * TopBar element.
	 * @type {HTMLElement|null}
	 */
	#topBar;

	/**
	 * Stores a reference to the body HTML element.
	 * @type {HTMLElement|null}
	 */
	#body;

	/**
	 * Header element.
	 * @type {HTMLElement|null}
	 */
	#header;

	/**
	 * Hamburger menu button.
	 * @type {HTMLElement|null}
	 */
	#hamburgerMenu;

	/**
	 * Mobile menu.
	 * @type {HTMLElement|null}
	 */
	#mobileMenuWrapper;

	/**
	 * Flag to prevent adding the global resize/media listeners more than once
	 * @type {boolean}
	 */
	#isGlobalListenerAdded = false;

	/**
	 * Desktop breakpoint value used in media queries
	 * @type {string}
	 */
	#desktopBreakpoint = '1200px';

	/**
	 * Creates an instance of a class.
	 *
	 * @return {void} Returns nothing.
	 */
	constructor() {

		this.#root = document.querySelector( 'html' );
		this.#topBar = document.querySelector( '.top-bar' );
		this.#body = document.querySelector( 'body' );
		this.#header = document.querySelector( '.page-header' );
		this.#mobileMenuWrapper = this.#header?.querySelector( '.header-menu-nav' );
		this.#hamburgerMenu = this.#header?.querySelector( '.js-hamburger-menu' );

		this.init();

	}

	/**
	 * Initializes header functionality.
	 *
	 * @return {void} Returns nothing.
	 */
	init = () => {

		this.setMainStyles();
		this.toggleMobileMenu();
		this.openMobileSubmenu();
		this.setupDesktopMediaWatcher();
		this.screenResize();
		document.addEventListener( 'click', this.handleOutsideClick );

	}

	/**
	 * Sets header styles.
	 *
	 * @return {void} Returns nothing.
	 */
	setMainStyles = () => {

		const headerHeight = this.#header?.offsetHeight;

		if ( !headerHeight || !this.#root ) {

			return;

		}

		// set header height
		this.#root.style.setProperty( '--header-height', `${headerHeight}px` );

		// set top bar height
		const topBarHeight = this.#topBar?.offsetHeight;

		if ( topBarHeight ) {

			this.#root.style.setProperty( '--top-bar-height', `${topBarHeight}px` );

		}

		this.setMegamenuHeight();
		this.setStandardMenuHeight();

	}

	/**
	 * Sets CSS variable (--submenu-desktop-height) for megamenu submenus
	 * based on the highest submenu (level 0/1/2) height.
	 *
	 * @return {void}
	 */
	setMegamenuHeight = () => {

		const megamenus = this.#header?.querySelectorAll( '.header-menu .submenu-type-megamenu' );

		if ( !megamenus.length ) {

			return;

		}

		megamenus.forEach( megamenu => {

			const submenus = megamenu.querySelectorAll( '.submenu-wrapper.level-1, .submenu-wrapper.level-2' );
			const submenLvl0 = megamenu.querySelector( '.submenu.depth-0' );

			if ( !submenus.length || !submenLvl0 ) {

				return;

			}

			let submenuHeight = 0;

			[...submenus, submenLvl0 ].forEach( submenu => {

				if ( submenu.scrollHeight > submenuHeight ) {

					submenuHeight = submenu.scrollHeight;

				}

			} )

			if ( submenuHeight > 0 ) {

				// Set submenu height same as higher submenu-wrapper on any level + submenu.depth-0 padding 24px
				const submenuNewHeight = submenuHeight + 24;
				megamenu.querySelector( '.submenu-wrapper.level-0' )?.style.setProperty( '--submenu-desktop-height', `${submenuNewHeight}px` );

			}

		} );

	}

	/**
	 * Sets CSS variable (--submenu-desktop-height) for standard menu submenus
	 * based on the highest featured article block inside the submenu.
	 *
	 * @return {void}
	 */
	setStandardMenuHeight = () => {

		const standadrMenus = this.#header?.querySelectorAll( '.header-menu .submenu-type-standard' );

		if ( !standadrMenus.length ) {

			return;

		}

		standadrMenus.forEach( standadrMenu => {

			const submenuPosts = standadrMenu.querySelectorAll( '.submenu-posts-wrapper .featured-article' );
			const submenLvl0 = standadrMenu.querySelector( '.submenu.depth-0' );

			if ( ! submenuPosts.length && !submenLvl0 ) {

				return;

			}

			let submenuHeight = 0;

			[...submenuPosts, submenLvl0 ].forEach( submenu => {

				if ( submenu.scrollHeight > submenuHeight ) {

					submenuHeight = submenu.scrollHeight;

				}

			} );

			if ( submenuHeight > 0 ) {

				// Set submenu height same as higher submenu-wrapper on any level + submenu.depth-0 padding 40px
				const submenuNewHeight = submenuHeight + 40;
				standadrMenu.querySelector( '.submenu-wrapper.level-0' )?.style.setProperty( '--submenu-desktop-height', `${submenuNewHeight}px` );

			}

		} );

	}

	/**
	 * Toggle mobile menu on click.
	 *
	 * @return {void} Returns nothing.
	 */
	toggleMobileMenu = () => {

		if ( ! this.#hamburgerMenu ) {

			return;

		}

		this.#hamburgerMenu.addEventListener( 'click', () => {

			this.#updateMobileMenuState();

		} );

	}

	/**
	 * Handles toggling the mobile menu and resets the top bar menu state.
	 * @return {void}
	 */
	#updateMobileMenuState = () => {

		this.#mobileMenuWrapper.classList.toggle( 'header-menu-nav--active' );
		this.#hamburgerMenu.classList.toggle( 'page-header__hamburger-menu--active' );
		this.#body.classList.toggle( 'no-scroll' );

		if ( this.#mobileMenuWrapper.classList.contains( 'header-menu-nav--active' ) ) {

			window.scrollTo( 0, 0 );

		}

		if ( !this.#hamburgerMenu.classList.contains( '.page-header__hamburger-menu--active' ) ) {

			this.clearSubmenus();

		}

	}

	/**
	 * Close the mobile menu and any opened submenus.
	 * @return {void}
	 */
	closeMobileMenu = () => {

		if ( ! this.#mobileMenuWrapper || ! this.#hamburgerMenu ) {

			return;

		}
		this.#mobileMenuWrapper.classList.remove( 'header-menu-nav--active' );
		this.#hamburgerMenu.classList.remove( 'page-header__hamburger-menu--active' );
		this.clearSubmenus();

	}

	/**
	 * Clears all active submenu states by removing the active class
	 * from all submenu wrappers within the mobile menu.
	 *
	 * @return {void}
	 */
	clearSubmenus = () => {

		this.#mobileMenuWrapper?.querySelectorAll( '.submenu-wrapper--active' )?.forEach( ( el ) => {

			el.classList.remove( 'submenu-wrapper--active' );

		} );

	}

	/**
	 * Initializes the mobile submenu interactions.
	 * Sets up the click event listener on the wrapper to handle
	 * back button clicks and menu item expansion.
	 *
	 * @return {void}
	 */
	openMobileSubmenu = () => {

		if ( !this.#mobileMenuWrapper ) {

			return;

		}

		this.#mobileMenuWrapper.addEventListener( 'click', ( event ) => {

			const clickedElement = event.target;

			if ( this.#handleBackButton( clickedElement ) ) {

				return;

			}

			if ( !this.#isGlobalListenerAdded ) {

				this.#isGlobalListenerAdded = true;
				document.addEventListener( 'click', this.handleOutsideClick );

			}

			this.#handleMenuItemClick( clickedElement, event );

		} );

	}

	/**
	 * Closes the parent submenu wrapper if the back button is clicked.
	 *
	 * @param {HTMLElement} clickedElement - The DOM element that was clicked.
	 * @return {boolean} - True if the back button was clicked and handled, false otherwise.
	 */
	#handleBackButton = ( clickedElement ) => {

		const backButton = clickedElement?.closest( '.submenu-back' );

		if ( !backButton ) {

			return false;

		}

		const wrapper = backButton.closest( '.submenu-wrapper' );
		wrapper?.classList.remove( 'submenu-wrapper--active' );

		return true;

	}

	/**
	 * Toggles the active state of submenus based on the viewport width (desktop vs mobile)
	 * and ensures sibling submenus are closed.
	 *
	 * @param {HTMLElement} clickedElement - The DOM element that was clicked.
	 * @param {Event} event - The original click event object.
	 * @return {void}
	 */
	#handleMenuItemClick = ( clickedElement, event ) => {

		const menuItemWithChildren = clickedElement?.closest( '.menu-item.menu-item-has-children' );

		if ( !menuItemWithChildren || !this.#mobileMenuWrapper.contains( menuItemWithChildren ) ) {

			return;

		}

		const submenuWrapper = menuItemWithChildren.querySelector( ':scope > .submenu-wrapper' );

		if ( window.matchMedia( `(min-width: ${this.#desktopBreakpoint})` ).matches ) {

			// Toggle submenu
			submenuWrapper?.classList.toggle( 'submenu-wrapper--active' );

			// Prevent clicks on parent links (with children) inside submenu-type-megamenu and on all elements at level 0 (desktop only)
			if ( clickedElement.closest( '.submenu-type-megamenu' ) || !clickedElement.closest( '.submenu.depth-0' ) ) {

				this.#preventLinkClik( clickedElement, event, menuItemWithChildren );

			}

		} else {

			submenuWrapper?.classList.add( 'submenu-wrapper--active' );
			this.#preventLinkClik( clickedElement, event, menuItemWithChildren );

		}

		submenuWrapper?.closest( '.submenu, .header-menu' ).querySelectorAll( '.menu-item-has-children > .submenu-wrapper--active' ).forEach( ( el ) => {

			if ( el !== submenuWrapper ) {

				// Close all submenus except the one that was clicked
				el.classList.remove( 'submenu-wrapper--active' );

			}

		} );

	}

	/**
	 * Prevents the default navigation behavior for parent menu links.
	 * This ensures that clicking the link or chevron opens the submenu instead of navigating.
	 *
	 * @param {HTMLElement} clickedElement - The actual element clicked (link or arrow).
	 * @param {Event} event - The click event to prevent default on.
	 * @param {HTMLElement} menuItemWithChildren - The parent container of the menu item.
	 * @return {void}
	 */
	#preventLinkClik = ( clickedElement, event, menuItemWithChildren ) => {

		const topLink = menuItemWithChildren.querySelector( ':scope > a' );
		const topChevron = menuItemWithChildren.querySelector( ':scope > img.c-menu-header-li-link-arrow' );

		if ( ( topLink?.contains( clickedElement ) ) || ( topChevron?.contains( clickedElement ) ) ) {

			event.preventDefault();

		}

	}


	/**
	 * Watch for viewport crossing into desktop and close mobile menu for performance.
	 * @return {void}
	 */
	setupDesktopMediaWatcher = () => {

		const mediaQuery = `(min-width: ${this.#desktopBreakpoint})`;
		const mediaQueryList = window.matchMedia( mediaQuery );

		mediaQueryList.addEventListener( 'change', event => {

			if ( event.matches ) {

				this.closeMobileMenu();

			}

		} );

	}

	screenResize = () => {

		let resizeDebounceTimer;

		window.addEventListener( 'resize', () => {

			clearTimeout( resizeDebounceTimer );

			resizeDebounceTimer = setTimeout( () => {

				this.setMainStyles();

			}, 200 );


		}, { passive: true } );

	}

	/**
	 * Handle clicks outside the header menu
	 *
	 * Closes submenus if clicked outside.
	 *
	 * @param {Event} e Click event.
	 * @return {void}
	 */
	handleOutsideClick = ( e ) => {

		if ( e.target.closest( '.page-header' ) ) {

			return;

		}

		this.clearSubmenus();
		this.#isGlobalListenerAdded = false;
		document.removeEventListener( 'click', this.handleOutsideClick );

	}

}
